import {Component, HostListener, Injector, Input, NgZone, ViewChild} from '@angular/core';
import {Company} from "../../models/company";
import {TableComponent} from "../ui/table/table.component";
import {CompanyService} from "../../services/company/company.service";
import {StatsService} from "../../services/stats/stats.service";
import {BaseComponent} from "../base/base.component";
import {ActivatedRoute} from "@angular/router";
import * as moment from 'moment';
import {getCompanyStatusMeta} from "../companies-settings/shared/company-status";
import {
    getCompanySubscriptionMeta,
    subscriptionToneBadgeClass,
} from "../companies-settings/shared/company-subscription";
import {DomainService} from "../../services/domain/domain.service";
import {promptCreateDomain} from "../admin-domains/create-domain-dialog";
import {OverlayPanel} from 'primeng/overlaypanel';
import {LazyLoadEvent} from 'primeng/api';
import {UserService} from '../../services/user/user.service';
import {User} from '../../models/user';

@Component({
    selector: 'app-company-list',
    templateUrl: './company-list.component.html',
    styleUrls: ['./company-list.component.scss']
})
export class CompaniesListComponent extends BaseComponent {
    @ViewChild('companiesTable') companiesTable?: TableComponent;
    @ViewChild('filtersPanel') filtersPanel?: OverlayPanel;

    stats: any;
    alternativeTable: boolean = false;
    companies: Company[] = [];
    totalRecords = 0;
    loading = true;
    first = 0;
    rows = 30;
    skeletonRows = [0, 1, 2, 3, 4, 5, 6, 7];

    textFilter = '';
    selectedStatusIds: string[] = [];
    selectedAgentIds: string[] = [];
    selectedPeriods: string[] = [];
    sortBy = 'createdAt desc';
    noDomain = false;
    noAgent = false;
    agents: {id: string; name: string}[] = [];
    lockedAgentId: string | null = null;
    private lastLazyEvent: LazyLoadEvent = {first: 0, rows: 30};

    public selectedMonth: number | null = null;
    public selectedYear: number | null = null;
    public currentParams: any = {includePendingApprovation: 1};
    public chartOptions: any;
    public chartReady: boolean = false;
    readonly chartSkeletonBars = ['42%', '68%', '35%', '88%', '54%', '72%', '48%', '91%', '63%', '40%', '77%', '58%'];
    private isTableLoading: boolean = false;

    readonly statusOptions = [
        {id: 'active', name: 'Approvata'},
        {id: 'pendingApprovation', name: 'In attesa di approvazione'},
        {id: 'pendingOnboarding', name: 'Onboarding in corso'},
        {id: 'pendingPayment', name: 'In attesa di pagamento'},
        {id: 'paymentFailed', name: 'Pagamento non riuscito'},
        {id: 'inactive', name: 'Disattivata'},
    ];
    readonly periodOptions = [
        {id: 'monthly', name: 'Mensile'},
        {id: 'yearly', name: 'Annuale'},
        {id: 'none', name: 'Nessun periodo'},
    ];
    readonly sortOptions = [
        {id: 'createdAt desc', name: 'Più recenti'},
        {id: 'createdAt asc', name: 'Più vecchie'},
        {id: 'name asc', name: 'Nome A–Z'},
        {id: 'name desc', name: 'Nome Z–A'},
    ];

    @Input('alternativeTable') alternativeTableLayout: boolean = false;
    @Input('showLabels') showLabels: boolean = true;

    constructor(public statsService: StatsService,
                public serviceInjector: Injector,
                public route: ActivatedRoute,
                public companyService: CompanyService,
                private domainService: DomainService,
                private userService: UserService,
                private zone: NgZone) {
        super(serviceInjector);
    }

    async ngOnInit() {
        if (this.route.snapshot.data['alternativeTableLayout']) {
            this.alternativeTable = this.route.snapshot.data['alternativeTableLayout'];
        }

        if (this.alternativeTableLayout) {
            this.alternativeTable = true;
        }

        this.lockedAgentId = this.route.snapshot.params['agentId'] || null;
        this.stats = (await this.statsService.getCompanyStatsTotal()).data;
        if (this.alternativeTable && !this.lockedAgentId) {
            const response = await this.userService.getUsers({
                userGroupId: 'agent',
                $top: 500,
                $orderby: 'firstName asc',
            });
            this.agents = (response.data ?? []).map((agent: User) => ({
                id: agent.id || '',
                name: `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || agent.id || 'Agente',
            })).filter((agent) => !!agent.id);
        }

        await this.loadChartData();
    }

    ngAfterViewInit(): void {
        if (this.route.snapshot.params['agentId']) {
            this.currentParams = {
                agentId: this.route.snapshot.params['agentId'],
                includePendingApprovation: 1
            };
        } else {
            this.currentParams = {
                includePendingApprovation: 1
            };
        }

        if (this.alternativeTable) return;

        setTimeout(() => {
            if (this.companiesTable) {
                this.companiesTable.loadData(
                    '/companies',
                    {...this.currentParams},
                    (item: any) => new Company(item)
                );
            }
        });
    }

    get activeFilterCount(): number {
        return [
            this.selectedStatusIds.length,
            this.selectedAgentIds.length,
            this.selectedPeriods.length,
            this.noDomain,
            this.noAgent,
            this.sortBy !== 'createdAt desc',
            !!(this.selectedMonth && this.selectedYear),
        ].filter(Boolean).length;
    }

    get hasActiveQuery(): boolean {
        return !!this.textFilter.trim() || this.activeFilterCount > 0;
    }

    get monthFilterLabel(): string {
        if (!this.selectedMonth || !this.selectedYear) return '';
        return moment().year(this.selectedYear).month(this.selectedMonth - 1).locale('it').format('MMMM YYYY');
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.filtersPanel?.overlayVisible) return;
        const target = event.target as HTMLElement | null;
        if (!target) return;
        if (
            target.closest('.user-filter-popover') ||
            target.closest('.user-filter-trigger') ||
            target.closest('.p-dropdown-panel') ||
            target.closest('.p-multiselect-panel')
        ) {
            return;
        }
        this.filtersPanel.hide();
    }

    listParams(first = this.first, rows = this.rows): Record<string, string | number> {
        const params: Record<string, string | number> = {
            $skip: first,
            $top: rows,
            includePendingApprovation: 1,
        };
        if (this.lockedAgentId) params['agentId'] = this.lockedAgentId;
        if (this.textFilter.trim()) params['text'] = this.textFilter.trim();
        if (this.selectedStatusIds.length) params['status'] = this.selectedStatusIds.join(',');
        if (!this.lockedAgentId && !this.noAgent && this.selectedAgentIds.length) {
            params['agentId'] = this.selectedAgentIds.join(',');
        }
        if (this.selectedPeriods.length) params['billedPeriod'] = this.selectedPeriods.join(',');
        if (this.noDomain) params['noDomain'] = 'true';
        if (this.noAgent && !this.lockedAgentId) params['noAgent'] = 'true';
        if (this.sortBy) params['$orderby'] = this.sortBy;
        if (this.selectedMonth && this.selectedYear) {
            params['creationMonth'] = this.selectedMonth;
            params['creationYear'] = this.selectedYear;
        }
        return params;
    }

    async loadCompanies(event?: LazyLoadEvent) {
        if (!this.alternativeTable) return;
        if (event) {
            this.lastLazyEvent = event;
            this.first = event.first ?? 0;
            this.rows = event.rows ?? 30;
        }

        this.loading = true;
        this.companies = [];
        const response = await this.companyService.getCompaniesPage(this.listParams());
        this.companies = (response.data ?? []).map((item) => new Company(item));
        this.totalRecords = response.count ?? this.companies.length;
        this.loading = false;
    }

    applyFilters() {
        this.first = 0;
        this.lastLazyEvent = {...this.lastLazyEvent, first: 0};
        this.filtersPanel?.hide();
        void this.loadCompanies(this.lastLazyEvent);
    }

    onNoAgentChange(enabled: boolean) {
        if (enabled) this.selectedAgentIds = [];
    }

    clearAdvancedFilters() {
        this.selectedStatusIds = [];
        this.selectedAgentIds = [];
        this.selectedPeriods = [];
        this.sortBy = 'createdAt desc';
        this.noDomain = false;
        this.noAgent = false;
        this.selectedMonth = null;
        this.selectedYear = null;
        this.applyFilters();
    }

    resetAll() {
        this.textFilter = '';
        this.clearAdvancedFilters();
    }

    async sendApprovalEmail(companyId: string) {
        const response = await this.companyService.sendApprovalEmail(companyId);

        if (response.success) {
            this.showSuccessMessage('Email di approvazione inviata con successo!')

        } else {
            this.showErrorMessage('Errore durante l\'operazione')
        }
    }

    async createDomainRecord(company: Company) {
        if (!company?.id) return;

        const values = await promptCreateDomain({
            name: company.domain,
            field: 'domain'
        });
        if (!values) return;

        this.showSpinner();
        const response = await this.domainService.addDomain(company.id, { name: values.name });
        this.hideSpinner();

        if (!response.success || !response.data?.length) {
            this.showErrorMessage(this.domainService.domainErrorMessage(response, 'Impossibile creare il record DNS'));
            return;
        }

        const createdShop = response.data.some((record) => record.field === 'shopDomain');
        const createdCompany = response.data.some((record) => record.field === 'domain');
        this.showSuccessMessage(
            createdCompany && createdShop
                ? 'Record DNS società e shop creati correttamente'
                : createdShop
                    ? 'Record DNS shop creato correttamente'
                    : 'Record DNS società creato correttamente'
        );
        if (this.alternativeTable) {
            void this.loadCompanies(this.lastLazyEvent);
        } else {
            this.companiesTable?.reloadPage();
        }
    }

    async loadChartData() {
        let params: any = {$top: 10000, includePendingApprovation: 1}
        if (this.route.snapshot.params['agentId']) {
            params.agentId = this.route.snapshot.params['agentId'];
        }
        const companiesArray = await this.companyService.getCompanies(params) || [];
        let now = moment();
        let startYear = now.year();
        const monthsLabels = [];
        const dataSeries = [];
        const monthMapData: { month: number, year: number }[] = [];
        const statsMap: { [key: string]: number } = {};
        for (const c of companiesArray) {
            if ((c.status === 'active') && c.createdAt) {
                const dt = moment(c.createdAt);
                const key = `${dt.month() + 1}-${dt.year()}`;
                statsMap[key] = (statsMap[key] || 0) + 1;
            }
        }
        for (let i = 0; i < 12; i++) {
            let m = moment().year(startYear).month(0).add(i, 'months');
            monthsLabels.push(m.locale('it').format('MMM YYYY').replace('.', '').replace(/^\w/, c => c.toUpperCase()));
            monthMapData.push({month: m.month() + 1, year: m.year()});
            const key = `${m.month() + 1}-${m.year()}`;
            dataSeries.push(statsMap[key] || 0);
        }
        this.chartOptions = {
            series: [{name: "Società attive create", data: dataSeries}],
            chart: {
                type: 'bar',
                height: 280,
                toolbar: {show: false},
                zoom: {
                    enabled: false
                },
                events: {
                    dataPointSelection: (event: any, chartContext: any, config: any) => {
                        const index = config.dataPointIndex;
                        const data = monthMapData[index];
                        this.zone.run(() => {
                            void this.filterByMonth(data.month, data.year);
                        });
                    }
                }
            },
            plotOptions: {bar: {horizontal: false, columnWidth: '50%', borderRadius: 8,},},
            dataLabels: {enabled: false},
            xaxis: {categories: monthsLabels,},
            yaxis: {title: {text: 'Numero società'}, min: 0, forceNiceScale: true, decimalsInFloat: 0},
            fill: {opacity: 1, colors: ['#00ae9e']},
            tooltip: {
                y: {
                    formatter: function (val: any) {
                        return val + " società";
                    }
                }
            },
            title: {text: 'Creazione società', align: 'left', style: {fontSize: '14px', fontWeight: 700, color: '#0f172a'}}
        };
        this.chartReady = true;
    }

    async filterByMonth(month: number, year: number): Promise<void> {
        if (this.isTableLoading) return;

        if (this.selectedMonth === month && this.selectedYear === year) {
            this.selectedMonth = null;
            this.selectedYear = null;
            delete this.currentParams.creationMonth;
            delete this.currentParams.creationYear;
        } else {
            this.selectedMonth = month;
            this.selectedYear = year;
            this.currentParams.creationMonth = month;
            this.currentParams.creationYear = year;
        }

        if (this.alternativeTable) {
            this.applyFilters();
            return;
        }

        if (!this.companiesTable) return;

        if (this.companiesTable?.searchText) {
            this.currentParams.text = this.companiesTable.searchText;
        } else {
            delete this.currentParams.text;
        }

        const params = {...this.currentParams};

        this.isTableLoading = true;
        this.showSpinner();
        try {
            await this.companiesTable.loadData(
                '/companies',
                params,
                (item: any) => new Company(item),
                true
            );
        } finally {
            this.isTableLoading = false;
            this.hideSpinner();
        }
    }

    companyStatusLabel(status?: string): string {
        return getCompanyStatusMeta(status).label;
    }

    companyStatusBadgeClass(status?: string): string {
        return subscriptionToneBadgeClass(getCompanyStatusMeta(status).tone);
    }

    companyStatusIcon(status?: string): string {
        switch (status) {
            case 'active':
            case 'approved': return 'bi-check-circle-fill';
            case 'inactive': return 'bi-slash-circle';
            case 'deleted': return 'bi-trash';
            case 'pendingApprovation': return 'bi-clock-history';
            case 'approving': return 'bi-arrow-repeat';
            case 'pendingOnboarding': return 'bi-hourglass-split';
            case 'pendingOnboardingOtp': return 'bi-shield-exclamation';
            case 'pendingOtp': return 'bi-shield-lock';
            case 'pendingPayment': return 'bi-credit-card';
            case 'paymentFailed': return 'bi-exclamation-circle-fill';
            default: return 'bi-question-circle';
        }
    }

    subscriptionLabel(company: Company): string {
        const meta = getCompanySubscriptionMeta(company);
        const planName = (company.service?.name ?? '').trim();
        if (meta.key === 'active' && planName) {
            if (company.billedPeriod === 'yearly') {
                return `${planName} annuale`;
            }
            if (company.billedPeriod === 'monthly') {
                return `${planName} mensile`;
            }
            return planName;
        }
        if (planName && meta.key !== 'missing') {
            return `${planName} · ${meta.label}`;
        }
        return meta.label;
    }

    subscriptionBadgeClass(company: Company): string {
        return subscriptionToneBadgeClass(getCompanySubscriptionMeta(company).tone);
    }

    subscriptionIcon(company: Company): string {
        switch (getCompanySubscriptionMeta(company).key) {
            case 'active': return 'bi-credit-card-2-front';
            case 'expired': return 'bi-calendar-x';
            case 'missing': return 'bi-dash-circle';
            case 'paymentFailed': return 'bi-exclamation-circle-fill';
            case 'pendingPayment': return 'bi-hourglass-split';
            case 'pastDue': return 'bi-clock-history';
            case 'invalid': return 'bi-exclamation-triangle-fill';
        }
    }

    agentRecord(company?: Company | null): User | null {
        const agent = company?.agent as User | User[] | undefined;
        if (!agent) return null;
        return Array.isArray(agent) ? agent[0] || null : agent;
    }

    agentLabel(company?: Company | null): string {
        const agent = this.agentRecord(company);
        if (!agent) return '';
        return `${agent.firstName || ''} ${agent.lastName || ''}`.trim();
    }

    openCompany(company: Company) {
        if (!company?.id) return;
        void this.navigate.navigate(['/companies', company.id, 'settings']);
    }
}
